import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useIdentityAuth } from '@auth/IdentityAuthContext';

// Generic fetch hook interface
interface UseFetchState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

// Fetch options interface
interface FetchOptions extends RequestInit {
  // Whether to include Access Identity authentication token
  useAuth?: boolean;
}

// Custom hook return type
interface UseFetchReturn<T> extends UseFetchState<T> {
  refetch: (
    path?: string,
    body?: unknown,
    customHeaders?: Record<string, string>
  ) => void;
}

/**
 * Custom hook for handling fetch API calls with loading, error, and data states
 * @param url - The URL to fetch from
 * @param options - Fetch options (method, headers, body, etc.)
 * @param immediate - Whether to fetch immediately on mount (default: true)
 * @returns Object containing data, loading, error states and refetch function
 */
export const useFetch = <T = unknown>(
  url: string,
  options: FetchOptions = {},
  immediate = true
): UseFetchReturn<T> => {
  const { getAccessToken, isAuthenticated, logout, login } = useIdentityAuth();
  const isLoggingOutRef = useRef(false);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(immediate);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // Stabilize options object to avoid unnecessary re-renders
  const optionsKey = JSON.stringify(options);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableOptions = useMemo(() => options, [optionsKey]);

  // Cleanup effect to cancel pending requests on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cancel any pending requests on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchData = useCallback(
    async (
      customPath?: string,
      customBody?: unknown,
      customHeaders?: Record<string, string>
    ) => {
      try {
        // Cancel previous request if still pending
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Create new AbortController for this request
        abortControllerRef.current = new AbortController();

        setLoading(true);
        setError(null);

        let authHeaders = {};

        // Add auth token if useAuth is true and user is authenticated.
        // If no valid token can be obtained (e.g. Safari blocks the silent-renew
        // iframe via ITP after the tab has been idle), do NOT send an anonymous
        // request — the API rejects it with 403 and the user gets stuck. Instead
        // re-authenticate via full-page redirect, which is not subject to ITP.
        if (stableOptions.useAuth && isAuthenticated) {
          let token: string | null = null;
          try {
            token = await getAccessToken();
          } catch (error) {
            console.warn('Failed to get Access Identity token:', error);
          }
          if (!token) {
            if (isMountedRef.current) {
              setLoading(false);
            }
            await login();
            return;
          }
          authHeaders = {
            Authorization: `Bearer ${token}`,
          };
        }

        // Remove useAuth from options before passing to fetch
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { useAuth, ...fetchOptions } = stableOptions;

        // Use custom path if provided, otherwise use original url
        const fetchUrl = customPath || url;

        const finalHeaders = {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
          ...fetchOptions.headers,
          ...authHeaders,
          ...customHeaders,
        };

        // Determine the final body - if customBody is provided, stringify it, otherwise use fetchOptions.body as-is
        const finalBody =
          customBody !== undefined
            ? JSON.stringify(customBody)
            : fetchOptions.body;

        const response = await fetch(fetchUrl, {
          ...fetchOptions,
          headers: finalHeaders,
          body: finalBody,
          cache: 'no-store',
          signal: abortControllerRef.current.signal, // Add abort signal
        });

        if (!response.ok) {
          if (
            response.status === 401 &&
            stableOptions.useAuth &&
            !isLoggingOutRef.current
          ) {
            isLoggingOutRef.current = true;
            console.warn(
              'Received 401 Unauthorized — session expired, logging out...'
            );
            await logout();
            return;
          }
          // Prefer the backend's structured error message (`fallbackErrorText`)
          // when present — otherwise fall back to the HTTP status line.
          let errorMessage = `HTTP error! status: ${response.status} - ${response.statusText}`;
          try {
            const errorBody = await response.text();
            if (errorBody) {
              try {
                const parsed = JSON.parse(errorBody);
                if (
                  parsed &&
                  typeof parsed.fallbackErrorText === 'string' &&
                  parsed.fallbackErrorText.trim() !== ''
                ) {
                  errorMessage = parsed.fallbackErrorText;
                } else if (
                  parsed &&
                  typeof parsed.message === 'string' &&
                  parsed.message.trim() !== ''
                ) {
                  errorMessage = parsed.message;
                }
              } catch {
                // Body wasn't JSON — use the raw text if it's short enough to be a message.
                if (errorBody.length < 500) {
                  errorMessage = errorBody;
                }
              }
            }
          } catch {
            // Body unreadable — keep the default HTTP status message.
          }
          throw new Error(errorMessage);
        }

        // Check if response has content before parsing
        const contentType = response.headers.get('content-type');
        let result: T;

        if (contentType && contentType.includes('application/json')) {
          const text = await response.text();
          result = text ? JSON.parse(text) : null;
        } else if (contentType && contentType.includes('text/')) {
          result = (await response.text()) as T;
        } else {
          result = (await response.blob()) as T;
        }

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setData(result);
        }
      } catch (err) {
        // Don't set error state if request was aborted
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        const errorMessage =
          err instanceof Error ? err.message : 'An unknown error occurred';

        if (isMountedRef.current) {
          setError(errorMessage);
          setData(null);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [url, stableOptions, getAccessToken, isAuthenticated, logout, login]
  );

  const refetch = useCallback(
    (path?: string, body?: unknown, customHeaders?: Record<string, string>) => {
      fetchData(path, body, customHeaders);
    },
    [fetchData]
  );

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [fetchData, immediate]);

  return {
    data,
    loading,
    error,
    refetch,
  };
};

/**
 * Specialized hook for GET requests
 * @param url - The URL to fetch from
 * @param headers - Additional headers
 * @param immediate - Whether to fetch immediately on mount (default: true)
 * @param useAuth - Whether to include Access Identity authentication token (default: false)
 */
export const useGet = <T = unknown>(
  url: string,
  headers: Record<string, string> = {},
  immediate = true,
  useAuth = false
): UseFetchReturn<T> => {
  return useFetch<T>(url, { method: 'GET', headers, useAuth }, immediate);
};

/**
 * Specialized hook for POST requests
 * @param url - The URL to post to
 * @param body - Request body
 * @param headers - Additional headers
 * @param immediate - Whether to fetch immediately on mount (default: false for POST)
 * @param useAuth - Whether to include Access Identity authentication token (default: false)
 */
export const usePost = <T = unknown>(
  url: string,
  body?: unknown,
  headers: Record<string, string> = {},
  immediate = false,
  useAuth = false
): UseFetchReturn<T> => {
  return useFetch<T>(
    url,
    {
      method: 'POST',
      headers,
      body: body ? JSON.stringify(body) : undefined,
      useAuth,
    },
    immediate
  );
};

/**
 * Specialized hook for PUT requests
 * @param url - The URL to put to (can be null for lazy initialization)
 * @param body - Request body
 * @param headers - Additional headers
 * @param immediate - Whether to fetch immediately on mount (default: false for PUT)
 * @param useAuth - Whether to include Access Identity authentication token (default: false)
 */
export const usePut = <T = unknown>(
  url: string | null,
  body?: unknown,
  headers: Record<string, string> = {},
  immediate = false,
  useAuth = false
): UseFetchReturn<T> => {
  // Always call the hook to satisfy rules of hooks
  const hookResult = useFetch<T>(
    url || '', // Use empty string as fallback
    {
      method: 'PUT',
      headers,
      body: body ? JSON.stringify(body) : undefined,
      useAuth,
    },
    immediate && !!url // Only fetch immediately if URL is provided
  );

  // If URL is null, return a modified version that forwards the actual state
  if (!url) {
    return {
      data: hookResult.data,
      loading: hookResult.loading,
      error: hookResult.error,
      refetch: (
        path?: string,
        customBody?: unknown,
        customHeaders?: Record<string, string>
      ) => {
        if (!path) {
          console.warn(
            'usePut: URL is required for refetch when initially null'
          );
          return;
        }
        // Call the actual refetch with the provided path
        hookResult.refetch(path, customBody, customHeaders);
      },
    };
  }

  return hookResult;
};

/**
 * Specialized hook for DELETE requests
 * @param url - The URL to delete from (can be null for lazy initialization)
 * @param headers - Additional headers
 * @param immediate - Whether to fetch immediately on mount (default: false for DELETE)
 * @param useAuth - Whether to include Access Identity authentication token (default: false)
 */
export const useDelete = <T = unknown>(
  url: string | null,
  headers: Record<string, string> = {},
  immediate = false,
  useAuth = false
): UseFetchReturn<T> => {
  const hookResult = useFetch<T>(
    url || '',
    {
      method: 'DELETE',
      headers,
      useAuth,
    },
    immediate && !!url
  );

  if (!url) {
    return {
      data: hookResult.data,
      loading: hookResult.loading,
      error: hookResult.error,
      refetch: (
        path?: string,
        body?: unknown,
        customHeaders?: Record<string, string>
      ) => {
        if (!path) {
          console.warn(
            'useDelete: URL is required for refetch when initially null'
          );
          return;
        }
        hookResult.refetch(path, body, customHeaders);
      },
    };
  }

  return hookResult;
};

// ---------------------------------------------------------------------------
// Keyed concurrent fetch
// ---------------------------------------------------------------------------

/** Per-key request lifecycle. */
export type KeyedFetchStatus = 'idle' | 'pending' | 'success' | 'error';

export interface KeyedFetchState<T> {
  status: KeyedFetchStatus;
  data: T | null;
  error: string | null;
}

export interface UseKeyedFetchReturn<T> {
  /** Live per-key state map. */
  states: Map<string, KeyedFetchState<T>>;
  /** Fire a request scoped to `key`. Re-firing the same key aborts the prior in-flight request for that key only. */
  execute: (key: string, url: string, body?: unknown, method?: string) => void;
  /** True while the request for `key` is in flight. */
  isPending: (key: string) => boolean;
  /** Keys currently in the `pending` state. */
  pendingKeys: Set<string>;
  /** Drop a key's state (e.g. after the caller has consumed a terminal success/error). */
  reset: (key: string) => void;
}

/**
 * Like {@link useFetch}, but tracks many concurrent requests keyed by a caller-supplied
 * string. Each key owns its own AbortController, so firing key "B" never cancels key "A".
 *
 * Reuses the same auth token flow, 401→logout handling, structured-error parsing and
 * unmount safety as {@link useFetch}.
 */
export const useKeyedFetch = <T = unknown>(): UseKeyedFetchReturn<T> => {
  const { getAccessToken, isAuthenticated, logout, login } = useIdentityAuth();
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const isLoggingOutRef = useRef(false);
  const isMountedRef = useRef(true);
  const [states, setStates] = useState<Map<string, KeyedFetchState<T>>>(
    new Map()
  );

  useEffect(() => {
    isMountedRef.current = true;
    const controllers = controllersRef.current;
    return () => {
      isMountedRef.current = false;
      // Abort every in-flight request on unmount.
      controllers.forEach(controller => controller.abort());
      controllers.clear();
    };
  }, []);

  const setKeyState = useCallback((key: string, next: KeyedFetchState<T>) => {
    if (!isMountedRef.current) return;
    setStates(prev => {
      const updated = new Map(prev);
      updated.set(key, next);
      return updated;
    });
  }, []);

  const deleteKeyState = useCallback((key: string) => {
    if (!isMountedRef.current) return;
    setStates(prev => {
      if (!prev.has(key)) return prev;
      const updated = new Map(prev);
      updated.delete(key);
      return updated;
    });
  }, []);

  const reset = useCallback(
    (key: string) => {
      controllersRef.current.delete(key);
      deleteKeyState(key);
    },
    [deleteKeyState]
  );

  const execute = useCallback(
    (key: string, url: string, body?: unknown, method = 'PUT') => {
      // Abort any prior request for THIS key only.
      const existing = controllersRef.current.get(key);
      if (existing) existing.abort();

      const controller = new AbortController();
      controllersRef.current.set(key, controller);
      setKeyState(key, { status: 'pending', data: null, error: null });

      void (async () => {
        try {
          let authHeaders: Record<string, string> = {};

          // Mirror useFetch: never fire an anonymous request when auth is expected.
          if (isAuthenticated) {
            let token: string | null = null;
            try {
              token = await getAccessToken();
            } catch (tokenError) {
              console.warn('Failed to get Access Identity token:', tokenError);
            }
            if (!token) {
              // Can't authenticate silently — clear pending and re-auth interactively.
              controllersRef.current.delete(key);
              deleteKeyState(key);
              await login();
              return;
            }
            authHeaders = { Authorization: `Bearer ${token}` };
          }

          const response = await fetch(url, {
            method,
            headers: {
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              Pragma: 'no-cache',
              Expires: '0',
              ...authHeaders,
            },
            body: body !== undefined ? JSON.stringify(body) : undefined,
            cache: 'no-store',
            signal: controller.signal,
          });

          if (!response.ok) {
            if (response.status === 401 && !isLoggingOutRef.current) {
              isLoggingOutRef.current = true;
              console.warn(
                'Received 401 Unauthorized — session expired, logging out...'
              );
              await logout();
              return;
            }
            // Prefer the backend's structured error message when present.
            let errorMessage = `HTTP error! status: ${response.status} - ${response.statusText}`;
            try {
              const errorBody = await response.text();
              if (errorBody) {
                try {
                  const parsed = JSON.parse(errorBody);
                  if (
                    parsed &&
                    typeof parsed.fallbackErrorText === 'string' &&
                    parsed.fallbackErrorText.trim() !== ''
                  ) {
                    errorMessage = parsed.fallbackErrorText;
                  } else if (
                    parsed &&
                    typeof parsed.message === 'string' &&
                    parsed.message.trim() !== ''
                  ) {
                    errorMessage = parsed.message;
                  }
                } catch {
                  if (errorBody.length < 500) {
                    errorMessage = errorBody;
                  }
                }
              }
            } catch {
              // Body unreadable — keep the default HTTP status message.
            }
            throw new Error(errorMessage);
          }

          const contentType = response.headers.get('content-type');
          let result: T | null = null;
          if (contentType && contentType.includes('application/json')) {
            const text = await response.text();
            result = text ? (JSON.parse(text) as T) : null;
          } else if (contentType && contentType.includes('text/')) {
            result = (await response.text()) as unknown as T;
          }

          controllersRef.current.delete(key);
          setKeyState(key, { status: 'success', data: result, error: null });
        } catch (err) {
          // A superseded/aborted request must not clobber the newer one's state.
          if (err instanceof Error && err.name === 'AbortError') {
            return;
          }
          const errorMessage =
            err instanceof Error ? err.message : 'An unknown error occurred';
          controllersRef.current.delete(key);
          setKeyState(key, {
            status: 'error',
            data: null,
            error: errorMessage,
          });
        }
      })();
    },
    [
      getAccessToken,
      isAuthenticated,
      logout,
      login,
      setKeyState,
      deleteKeyState,
    ]
  );

  const pendingKeys = useMemo(() => {
    const keys = new Set<string>();
    states.forEach((state, key) => {
      if (state.status === 'pending') keys.add(key);
    });
    return keys;
  }, [states]);

  const isPending = useCallback(
    (key: string) => states.get(key)?.status === 'pending',
    [states]
  );

  return { states, execute, isPending, pendingKeys, reset };
};

export default useFetch;
